import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { autoApi } from '@/AdminPanel/api/auto.api';
import type {
  TBrand,
  TCreateBrand,
  TCreateEquipment,
  TEquipment,
  TModel,
  TModelCreate
} from '@/AdminPanel/pages/Auto/types/auto.types';
import { DictTypes } from '@/shared/constants';
import { getOptimizedDictionaries } from '@/store/dict.slice';

type TAutoSlice = {
  isLoading: boolean;
  brands: TBrand[];
  models: TModel[];
  equipments: TEquipment[];
  total: number;
  brand: TBrand | null;
  model: TModel | null;
  equipment: TEquipment | null;
};

const initialState: TAutoSlice = {
  isLoading: false,
  brands: [],
  models: [],
  equipments: [],
  total: 0,
  brand: null,
  model: null,
  equipment: null
};

export const getBrands = createAsyncThunk(
  'admin/brands',
  async ({
    limit,
    page,
    searchTerm
  }: {
    limit?: number;
    page?: number;
    searchTerm?: string;
  }) => {
    return await autoApi.getBrands(limit, page, searchTerm);
  }
);

export const getModels = createAsyncThunk(
  'admin/models',
  async ({
    limit,
    page,
    searchTerm,
    brand,
    isCommercial
  }: {
    limit?: number;
    page?: number;
    searchTerm?: string;
    brand?: string;
    isCommercial?: boolean;
  }) => {
    return autoApi.getModels(limit, page, searchTerm, brand, isCommercial);
  }
);

export const getEquipments = createAsyncThunk(
  'admin/equipments',
  async ({
    limit,
    page,
    searchTerm,
    equipmentsTypeUuid,
    isActive
  }: {
    limit?: number;
    page?: number;
    searchTerm?: string;
    equipmentsTypeUuid?: string;
    isActive?: boolean;
  }) => {
    return autoApi.getEquipments(
      limit,
      page,
      searchTerm,
      equipmentsTypeUuid,
      isActive
    );
  }
);

export const createModels = createAsyncThunk(
  'admin/module/create',
  async ({ data }: { data: TModelCreate }, { dispatch }) => {
    const response = await autoApi.createModels(data);
    dispatch(getOptimizedDictionaries({ dictTypes: [DictTypes.Models] }));
    return response;
  }
);

export const updateModels = createAsyncThunk(
  'admin/models/update',
  async (
    { request_id, data }: { request_id: string; data: TModelCreate },
    { dispatch }
  ) => {
    const response = await autoApi.updateModels(request_id, data);
    dispatch(getOptimizedDictionaries({ dictTypes: [DictTypes.Models] }));
    return response;
  }
);

export const getModelsById = createAsyncThunk(
  'admin/modelsId',
  async (modelId: string) => {
    return autoApi.getAdminModelId(modelId);
  }
);

export const updateEquipments = createAsyncThunk(
  'admin/equipments/update',
  async ({
    request_id,
    data
  }: {
    request_id: string;
    data: TCreateEquipment;
  }) => {
    const response = await autoApi.updateEquipments(request_id, data);
    return response;
  }
);

export const getEquipmentById = createAsyncThunk(
  'admin/equipmentId',
  async (equipmentId: string) => {
    return autoApi.getAdminEquipmentId(equipmentId);
  }
);

export const createBrands = createAsyncThunk(
  'admin/brands/create',
  async (data: TCreateBrand, { dispatch }) => {
    const response = await autoApi.createBrands(data);
    dispatch(getOptimizedDictionaries({ dictTypes: [DictTypes.Brands] }));
    return response;
  }
);

export const updateBrands = createAsyncThunk(
  'admin/brands/update',
  async (data: TCreateBrand, { dispatch }) => {
    const { uuid, ...rest } = data;
    const response = await autoApi.updateBrands(uuid || '', rest);
    dispatch(getOptimizedDictionaries({ dictTypes: [DictTypes.Brands] }));
    return response;
  }
);

export const getBrandsById = createAsyncThunk(
  'admin/brandsId',
  async (brandId: string) => {
    return autoApi.getAdminBrandId(brandId);
  }
);

export const createEquipments = createAsyncThunk(
  'admin/equipments/create',
  async (data: TCreateEquipment) => {
    const response = await autoApi.createEquipments(data);
    return response;
  }
);

const adminAutoSlice = createSlice({
  name: 'admin/auto',
  initialState: initialState,
  reducers: {
    resetAll: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBrands.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getBrands.fulfilled, (state, action) => {
        state.brands = action.payload.data;
        state.total = action.payload.total;
        state.isLoading = false;
      })
      .addCase(getBrands.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getModels.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getModels.fulfilled, (state, action) => {
        state.models = action.payload.data;
        state.total = action.payload.total;
        state.isLoading = false;
      })
      .addCase(getModels.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getEquipments.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getEquipments.fulfilled, (state, action) => {
        state.equipments = action.payload.data;
        state.total = action.payload.total;
        state.isLoading = false;
      })
      .addCase(getEquipments.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getBrandsById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.brand = action.payload;
      })
      .addCase(getBrandsById.pending, (state) => {
        state.isLoading = true;
        state.brand = null;
      })
      .addCase(getBrandsById.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getModelsById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.model = action.payload;
      })
      .addCase(getModelsById.pending, (state) => {
        state.isLoading = true;
        state.model = null;
      })
      .addCase(getModelsById.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getEquipmentById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.equipment = action.payload;
      })
      .addCase(getEquipmentById.pending, (state) => {
        state.isLoading = true;
        state.equipment = null;
      })
      .addCase(getEquipmentById.rejected, (state) => {
        state.isLoading = false;
      });
  }
});

export const { resetAll } = adminAutoSlice.actions;
export default adminAutoSlice;
